
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useSearchParams } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import MissionPage from "./pages/MissionPage";
import GetInvolvedPage from "./pages/GetInvolvedPage";
import NewsPage from "./pages/NewsPage";
import NewsDetailPage from "./pages/NewsDetailPage";
import ResourcesPage from "./pages/ResourcesPage";
import ContactPage from "./pages/ContactPage";
import JoinPage from "./pages/JoinPage";
import CommunityPage from "./pages/CommunityPage";
import AdminPage from "./pages/AdminPage";
import StatementGeneratorPage from "./pages/StatementGeneratorPage";
import NotFound from "./pages/NotFound";
import TopicDetail from "./components/community/Hub/TopicDetail";
import PageTransition from "./components/PageTransition";
import { AuthProvider } from "./context/AuthContext";
import ProverbWidget from "./components/ProverbWidget";
import ExternalRedirect from "./components/utility/ExternalRedirect";
import { useEffect } from "react";

const queryClient = new QueryClient();

// Global redirect function - can be called from anywhere
export const redirectToOmoduduwa = () => {
  console.log("GLOBAL REDIRECT: Redirecting to omooduduwa.org/join");
  window.location.replace('https://www.omooduduwa.org/join');
};

// Immediate execution to check URLs before React even loads
(function() {
  try {
    const currentUrl = window.location.href;
    const pathname = window.location.pathname || '';
    
    // If the URL contains our target pattern, redirect immediately
    if (currentUrl && currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195')) {
      console.log("IMMEDIATE EXECUTION: Special URL pattern found, redirecting now");
      window.location.replace('https://www.omooduduwa.org/join');
    }
    
    // Also check URL parameters directly
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = urlParams.get('referrer');
    if (referrer === '21d11b63-7b91-46fa-8c91-a30744574195') {
      console.log("IMMEDIATE EXECUTION: Special referrer parameter found, redirecting now");
      window.location.replace('https://www.omooduduwa.org/join');
    }
    
    // Additional check for the exact path /join with our ID
    if (pathname === '/join' && 
        (referrer === '21d11b63-7b91-46fa-8c91-a30744574195' || 
         currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195'))) {
      console.log("IMMEDIATE EXECUTION: Join page with special ID detected, redirecting now");
      window.location.replace('https://www.omooduduwa.org/join');
    }
  } catch (e) {
    console.error("Error in immediate execution check:", e);
  }
})();

// Root level redirect handler for special URLs and referrer IDs
const RootRedirectHandler = ({ children }: { children: React.ReactNode }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const referrer = searchParams.get('referrer');
  
  // Check for special redirect case immediately
  useEffect(() => {
    const currentUrl = window.location.href;
    const pathname = location.pathname;
    
    console.log("RootRedirectHandler: Current URL:", currentUrl);
    console.log("RootRedirectHandler: Current pathname:", pathname);
    
    // Handle the direct URL case
    if (currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195')) {
      console.log("RootRedirectHandler: Special URL pattern detected. Redirecting now.");
      redirectToOmoduduwa();
      return;
    } 
    // Handle the referrer parameter case
    else if (referrer === '21d11b63-7b91-46fa-8c91-a30744574195') {
      console.log("RootRedirectHandler: Special referrer parameter detected. Redirecting now.");
      redirectToOmoduduwa();
      return;
    }
    // Handle the specific join page case
    else if (pathname === '/join' && 
            (referrer === '21d11b63-7b91-46fa-8c91-a30744574195' || 
             currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195'))) {
      console.log("RootRedirectHandler: Join page with special ID detected. Redirecting now.");
      redirectToOmoduduwa();
      return;
    }
  }, [referrer, location]);
  
  // Define special redirect mappings for specific referrer IDs
  const specialRedirectMappings = [
    {
      param: 'referrer',
      value: '21d11b63-7b91-46fa-8c91-a30744574195',
      url: 'https://www.omooduduwa.org/join',
      // Don't specify full domain, just the path pattern to match
      fullPath: '/join?referrer=21d11b63-7b91-46fa-8c91-a30744574195'
    }
  ];
  
  return (
    <ExternalRedirect
      paramName="referrer"
      redirectMappings={specialRedirectMappings}
      immediate={true}
      checkFullPath={true}
    >
      {children}
    </ExternalRedirect>
  );
};

const AppContent = () => {
  // Check for direct URL match at the app level
  useEffect(() => {
    const currentUrl = window.location.href;
    if (currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195')) {
      console.log("App-level URL check: Special URL pattern found. Redirecting now.");
      redirectToOmoduduwa();
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <RootRedirectHandler>
        <Navbar />
        <main className="flex-grow">
          <PageTransition>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/mission" element={<MissionPage />} />
              <Route path="/get-involved" element={<GetInvolvedPage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:id" element={<NewsDetailPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/join" element={<JoinPage />} />
              <Route path="/statement-generator" element={<StatementGeneratorPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/hub/:topicId" element={<TopicDetail />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </PageTransition>
        </main>
        <Footer />
        <ProverbWidget />
      </RootRedirectHandler>
    </div>
  );
};

const App = () => {
  // Final check at the very root level
  useEffect(() => {
    const currentUrl = window.location.href;
    if (currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195')) {
      console.log("Root-level URL check: Special URL pattern found. Redirecting now.");
      redirectToOmoduduwa();
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
